
import React, { useState } from 'react';
import { Product } from '../types';
import { getSensoryProfile, SensoryProfile } from '../services/geminiService';
import { Loader2, Info, Coffee, Sun, Tag } from 'lucide-react';

interface ProductCardProps {
  product: Product;
  badge?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, badge }) => {
  const [profile, setProfile] = useState<SensoryProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const fetchProfile = async () => {
    if (profile) {
      setShowProfile(!showProfile);
      return;
    }
    setLoading(true);
    try {
      const data = await getSensoryProfile(product.name, product.description);
      setProfile(data);
      setShowProfile(true);
    } catch (error) {
      console.error("Error fetching sensory profile", error);
    } finally {
      setLoading(false);
    }
  };

  const baseImageUrl = product.image.split('&w=')[0];

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-stone-100 flex flex-col h-full relative group">
      <div className="relative h-48 overflow-hidden">
        <img 
          src={`${baseImageUrl}&w=600`}
          srcSet={`${baseImageUrl}&w=300 300w, ${baseImageUrl}&w=600 600w, ${baseImageUrl}&w=900 900w`}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
          alt={product.name} 
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors duration-300" />
        
        {/* MINI ETIQUETA DE PRECIO Y PESO (SOLICITADO) */}
        <div className="absolute top-3 right-3 z-10">
          <div className="bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-lg border border-stone-100 shadow-lg flex items-center gap-1.5 animate-in slide-in-from-right-4 duration-500">
            <span className="text-amber-600 font-bold text-[11px]">$</span>
            <span className="text-stone-900 font-bold text-[11px] tracking-tight">
              {typeof product.price === 'number' ? product.price.toLocaleString('es-CO') : product.price} / {product.weight}
            </span>
          </div>
        </div>

        {badge && (
          <span className="absolute top-3 left-3 bg-stone-800 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm shadow-lg">
            {badge}
          </span>
        )}
        
        <button 
          onClick={fetchProfile}
          disabled={loading}
          className="absolute bottom-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full text-stone-800 hover:bg-amber-400 hover:text-white transition-all shadow-md active:scale-95 disabled:opacity-50"
          title="Descubrir experiencia sensorial"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
        </button>
      </div>

      <div className="p-5 flex-grow flex flex-col">
        <h3 className="text-xl font-bold text-stone-800 mb-1 leading-tight">{product.name}</h3>
        
        {showProfile && profile ? (
          <div className="mt-2 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="text-[11px] leading-relaxed text-stone-500 italic border-l-2 border-amber-200 pl-3">
              "{profile.notes}"
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-stone-50">
              <div className="flex items-center gap-2">
                <Coffee size={14} className="text-amber-600" />
                <span className="text-[10px] font-bold text-stone-700 uppercase">{profile.pairing}</span>
              </div>
              <div className="flex items-center gap-2">
                <Sun size={14} className="text-amber-500" />
                <span className="text-[10px] font-bold text-stone-700 uppercase">{profile.mood}</span>
              </div>
            </div>
            <button 
              onClick={() => setShowProfile(false)}
              className="text-[10px] uppercase tracking-tighter font-bold text-stone-400 hover:text-stone-600"
            >
              Cerrar detalles
            </button>
          </div>
        ) : (
          <>
            <p className="text-stone-500 text-sm leading-relaxed mb-4 font-light">
              {product.description}
            </p>
            
            <div className="mt-auto pt-4 border-t border-stone-50">
              <p className="text-[10px] text-stone-400 uppercase tracking-[0.2em] mb-1 font-bold">
                {product.benefit ? 'Valor Diferencial' : 'El Secreto'}
              </p>
              <p className={`text-sm font-semibold ${product.benefit ? 'text-emerald-700' : 'text-stone-700'}`}>
                {product.differentialValue}
              </p>
              {product.benefit && (
                <p className="text-[11px] text-stone-500 mt-1 leading-snug">
                  {product.benefit}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
